/**
 * Address Aliases, laid out by Stargard source file (Ian, 2026-09-23 — he
 * could not tell which store's file "main lot" came from under the old
 * file tabs). Each store's file is a collapsible group listing what that file
 * said on the latest list run and the lot each text became, then — clearly
 * separated underneath — the texts no rule covers yet, each with a lot picker
 * that adds a rule for that file. Below the files: the rules no text on that
 * run used, and a form to add any rule by hand.
 *
 * The grouping and rule resolution come from the server
 * (settings.stargard_seen, services/photo_list_settings.py
 * group_stargard_texts_by_file), which resolves exactly as the list generator
 * does — this component never re-implements the matching.
 */
import React, { useMemo, useState } from "react"
import { ChevronDown, ChevronRight, Trash2 } from "lucide-react"

import {
  PhotoListLot,
  PhotoListStargardAlias,
  PhotoListStargardSeen,
  PhotoListStargardSeenFile,
  PhotoListStargardSeenText,
  createStargardAlias,
  deleteStargardAlias,
  updateStargardAlias,
} from "../../api/photoList"
import { THEME } from "../../config/theme"
import StargardSeenRuleRow, {
  EVERY_STORE_FILE,
  NO_RULE_RESULT,
  RuleUse,
  scopeLabel,
} from "./StargardSeenRuleRow"
import {
  addButtonStyle,
  captionStyle,
  cellTextStyle,
  countStyle,
  fileBodyStyle,
  fileGroupStyle,
  fileHeaderStyle,
  fileHeaderSummaryStyle,
  hintTextStyle,
  inputStyle,
  monoInputStyle,
  noRuleGridStyle,
  noRuleGroupStyle,
  noRuleSummaryStyle,
  noRuleTitleStyle,
  panelStyle,
  panelSubtitleStyle,
  panelTitleStyle,
  rawTextStyle,
  ruleGridStyle,
  smallButtonStyle,
} from "./stargardAliasStyles"

/** The every-store scope value — matches the backend's any-file "". */
/** "No rule yet" rows shown before "Show all" — most are one-car addresses. */
const NO_RULE_PREVIEW_COUNT = 15

type RealLot = PhotoListLot & { id: number }

interface Props {
  lots: PhotoListLot[]
  aliases: PhotoListStargardAlias[]
  stargardSeen: PhotoListStargardSeen | null
  /** The six Stargard file names, server-ordered — the valid rule scopes. */
  stargardFileOptions: string[]
  editable: boolean
  isMobile: boolean
  dealerGroupId: number
  onStructureChanged: () => void | Promise<void>
  onError: (message: string | null) => void
}

// Same wording as the Daily Photo List's run dropdown, so both pages name a
// run identically (DailyPhotoListPage formatRunLabel).
function formatRunTime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? "" : "s"}`
}

export default function StargardAliasesByFile({
  lots,
  aliases,
  stargardSeen,
  stargardFileOptions,
  editable,
  isMobile,
  dealerGroupId,
  onStructureChanged,
  onError,
}: Props) {
  const realLots = lots.filter((lot): lot is RealLot => lot.id !== null)
  // With no complete run to lay out, the rules can't be split into seen /
  // not seen, so every rule is listed ("All rules"); with no run at all it's
  // all there is — so it starts open.
  const allRulesMode = stargardSeen === null || !stargardSeen.complete
  const rulesNotSeen = allRulesMode ? aliases : stargardSeen.rules_not_seen

  // Which store files each rule covered on the latest run — the editor names
  // them before a shared rule changes.
  const usesByAliasId = useMemo(() => {
    const uses = new Map<number, RuleUse[]>()
    for (const group of stargardSeen?.files ?? []) {
      for (const row of group.rules) {
        if (row.alias_id === null) continue
        const list = uses.get(row.alias_id) ?? []
        // A file that spells the text two ways is still one file.
        const sameFile = list.find((use) => use.file === group.file)
        if (sameFile) {
          sameFile.cars += row.cars
        } else {
          list.push({ file: group.file, cars: row.cars })
        }
        uses.set(row.alias_id, list)
      }
    }
    return uses
  }, [stargardSeen])

  return (
    <section style={panelStyle}>
      <div style={panelTitleStyle}>Address Aliases</div>
      <p style={panelSubtitleStyle}>
        What each store's Stargard file said on the latest list run, and the
        lot each text became. A rule covers one store's file or every store's
        file; a rule on a store's own file wins. Matching ignores capital
        letters and spaces at the start or end.
      </p>

      {stargardSeen ? (
        <p style={hintTextStyle}>
          From the list run on {formatRunTime(stargardSeen.run_started_at)}.
          {!stargardSeen.complete &&
            " That run is from before INVI kept every Stargard text, so only the texts with no rule are shown — the next list run fills in the rest."}
        </p>
      ) : (
        <p style={hintTextStyle}>
          No photo list has run for this group yet — once one does, each
          store's file appears here. The rules themselves are listed below.
        </p>
      )}

      {stargardSeen?.files.map((group) => (
        <StoreFileGroup
          key={group.file || "unidentified"}
          group={group}
          lots={realLots}
          aliases={aliases}
          usesByAliasId={usesByAliasId}
          knownFile={stargardFileOptions.includes(group.file)}
          complete={stargardSeen.complete}
          editable={editable}
          isMobile={isMobile}
          dealerGroupId={dealerGroupId}
          onStructureChanged={onStructureChanged}
          onError={onError}
        />
      ))}

      <RulesNotSeenGroup
        title={allRulesMode ? "All rules" : "Rules not seen in the latest run"}
        rules={rulesNotSeen}
        startOpen={stargardSeen === null}
        lots={realLots}
        editable={editable}
        isMobile={isMobile}
        dealerGroupId={dealerGroupId}
        onStructureChanged={onStructureChanged}
        onError={onError}
      />

      {editable && (
        <AddRuleForm
          lots={realLots}
          stargardFileOptions={stargardFileOptions}
          isMobile={isMobile}
          dealerGroupId={dealerGroupId}
          onStructureChanged={onStructureChanged}
          onError={onError}
        />
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// One store's file
// ---------------------------------------------------------------------------

function StoreFileGroup({
  group,
  lots,
  aliases,
  usesByAliasId,
  knownFile,
  complete,
  editable,
  isMobile,
  dealerGroupId,
  onStructureChanged,
  onError,
}: {
  group: PhotoListStargardSeenFile
  lots: RealLot[]
  aliases: PhotoListStargardAlias[]
  usesByAliasId: Map<number, RuleUse[]>
  /** False for a source the server couldn't match to one of the six files. */
  knownFile: boolean
  /** False for a run from before INVI kept every text: only its texts with
   * no rule are known, so car totals and "not in this run" would mislead. */
  complete: boolean
  editable: boolean
  isMobile: boolean
  dealerGroupId: number
  onStructureChanged: () => void | Promise<void>
  onError: (message: string | null) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [showAllNoRule, setShowAllNoRule] = useState(false)
  const fileName = group.file || "Unidentified"
  const noRuleCars = group.no_rule.reduce((sum, row) => sum + row.cars, 0)
  const visibleNoRule = showAllNoRule ? group.no_rule : group.no_rule.slice(0, NO_RULE_PREVIEW_COUNT)
  const Chevron = expanded ? ChevronDown : ChevronRight

  return (
    <div style={fileGroupStyle}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        style={fileHeaderStyle}
      >
        <Chevron size={14} strokeWidth={2} style={{ color: THEME.colors.neutral.light.textSecondary }} />
        {fileName}'s file
        {!complete ? (
          <span style={group.no_rule.length > 0 ? noRuleSummaryStyle : fileHeaderSummaryStyle}>
            — {plural(group.no_rule.length, "text")} with no rule on that run
          </span>
        ) : group.cars === 0 ? (
          <span style={fileHeaderSummaryStyle}>— not in this run</span>
        ) : (
          <>
            <span style={fileHeaderSummaryStyle}>
              — {plural(group.cars, "car")} · {plural(group.rules.length, "text")} with a rule
            </span>
            {group.no_rule.length > 0 && (
              <span style={noRuleSummaryStyle}>· {group.no_rule.length} with no rule yet</span>
            )}
          </>
        )}
      </button>

      {expanded && (
        <div style={fileBodyStyle}>
          {group.rules.length > 0 && !isMobile && (
            <div style={{ ...ruleGridStyle(false), borderTop: "none" }}>
              <span style={captionStyle}>Stargard says</span>
              <span style={captionStyle}>Becomes lot</span>
              <span style={{ ...captionStyle, textAlign: "right" }}>Cars</span>
              <span style={captionStyle}>Rule covers</span>
              <span />
            </div>
          )}
          {group.rules.map((row) => (
            <StargardSeenRuleRow
              // The lot is in the key: when another edit re-points this rule,
              // an editor left open here remounts instead of saving a stale lot.
              key={`${row.rule_scope}:${row.raw}:${row.lot_id}`}
              row={row}
              file={group.file}
              fileScopeAvailable={knownFile}
              lots={lots}
              aliases={aliases}
              usesByAliasId={usesByAliasId}
              editable={editable}
              isMobile={isMobile}
              dealerGroupId={dealerGroupId}
              onStructureChanged={onStructureChanged}
              onError={onError}
            />
          ))}
          {complete && group.rules.length === 0 && group.cars > 0 && (
            <p style={hintTextStyle}>No text from this file matched a rule.</p>
          )}

          {group.no_rule.length > 0 && (
            <div style={noRuleGroupStyle}>
              <div style={noRuleTitleStyle}>
                No rule yet · {plural(group.no_rule.length, "text")} · {plural(noRuleCars, "car")}
              </div>
              <p style={hintTextStyle}>
                These cars show {NO_RULE_RESULT} in the Stargard column.
                {editable &&
                  (knownFile
                    ? ` Pick a lot to add a rule for ${fileName}'s file.`
                    : " Pick a lot to add a rule for every store's file.")}
              </p>
              {visibleNoRule.map((row) => (
                <NoRuleRow
                  key={row.raw}
                  row={row}
                  file={knownFile ? group.file : EVERY_STORE_FILE}
                  fileName={fileName}
                  lots={lots}
                  editable={editable}
                  isMobile={isMobile}
                  dealerGroupId={dealerGroupId}
                  onStructureChanged={onStructureChanged}
                  onError={onError}
                />
              ))}
              {group.no_rule.length > NO_RULE_PREVIEW_COUNT && (
                <button
                  type="button"
                  onClick={() => setShowAllNoRule(!showAllNoRule)}
                  style={{ ...smallButtonStyle(), marginTop: THEME.spacing.sm }}
                >
                  {showAllNoRule ? "Show fewer" : `Show all ${group.no_rule.length}`}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function NoRuleRow({
  row,
  file,
  fileName,
  lots,
  editable,
  isMobile,
  dealerGroupId,
  onStructureChanged,
  onError,
}: {
  row: PhotoListStargardSeenText
  /** The rule scope a new rule gets: this file, or "" when the file is unidentified. */
  file: string
  fileName: string
  lots: RealLot[]
  editable: boolean
  isMobile: boolean
  dealerGroupId: number
  onStructureChanged: () => void | Promise<void>
  onError: (message: string | null) => void
}) {
  const [lotId, setLotId] = useState("")
  const [adding, setAdding] = useState(false)

  const handleAdd = async () => {
    const parsed = Number.parseInt(lotId, 10)
    if (Number.isNaN(parsed)) return
    setAdding(true)
    onError(null)
    try {
      // The rule lands on the file that said it — the same text can mean a
      // different place in another store's export.
      await createStargardAlias(row.raw, parsed, file, dealerGroupId)
      // Awaited so the button stays busy until the row has moved.
      await onStructureChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to add the rule")
    } finally {
      setAdding(false)
    }
  }

  const picker = editable ? (
    <select
      value={lotId}
      onChange={(e) => setLotId(e.target.value)}
      aria-label={`Lot for ${row.raw}`}
      style={inputStyle}
    >
      <option value="">Pick a lot…</option>
      {lots.map((lot) => (
        <option key={lot.id} value={lot.id}>
          {lot.name}
        </option>
      ))}
    </select>
  ) : (
    <span />
  )
  const addButton = editable ? (
    <button
      type="button"
      onClick={handleAdd}
      disabled={!lotId || adding}
      style={addButtonStyle(!lotId || adding)}
    >
      {adding ? "Adding…" : file ? `Add for ${fileName}` : "Add for every store"}
    </button>
  ) : (
    <span />
  )

  if (isMobile) {
    return (
      <div style={noRuleGridStyle(true)}>
        <div style={{ display: "flex", flexDirection: "column", gap: THEME.spacing.xs, minWidth: 0 }}>
          <span style={rawTextStyle}>{row.raw}</span>
          {picker}
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: THEME.spacing.xs }}>
          <span style={countStyle}>{plural(row.cars, "car")}</span>
          {addButton}
        </div>
      </div>
    )
  }
  return (
    <div style={noRuleGridStyle(false)}>
      <span style={rawTextStyle}>{row.raw}</span>
      {picker}
      <span style={countStyle}>{row.cars}</span>
      {addButton}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Rules the run didn't use, and adding a rule by hand
// ---------------------------------------------------------------------------

function RulesNotSeenGroup({
  title,
  rules,
  startOpen,
  lots,
  editable,
  isMobile,
  dealerGroupId,
  onStructureChanged,
  onError,
}: {
  title: string
  rules: PhotoListStargardAlias[]
  startOpen: boolean
  lots: RealLot[]
  editable: boolean
  isMobile: boolean
  dealerGroupId: number
  onStructureChanged: () => void | Promise<void>
  onError: (message: string | null) => void
}) {
  const [expanded, setExpanded] = useState(startOpen)
  if (rules.length === 0) return null
  const Chevron = expanded ? ChevronDown : ChevronRight

  return (
    <div style={fileGroupStyle}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        style={fileHeaderStyle}
      >
        <Chevron size={14} strokeWidth={2} style={{ color: THEME.colors.neutral.light.textSecondary }} />
        {title}
        <span style={fileHeaderSummaryStyle}>— {plural(rules.length, "rule")}</span>
      </button>
      {expanded && (
        <div style={fileBodyStyle}>
          {rules.map((alias) => (
            <RuleRow
              key={alias.id ?? `default-${alias.stargard_rooftop}-${alias.raw_text}`}
              alias={alias}
              lots={lots}
              editable={editable && alias.id !== null}
              isMobile={isMobile}
              dealerGroupId={dealerGroupId}
              onStructureChanged={onStructureChanged}
              onError={onError}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/** Edit-in-place rule editor (the old tabbed panel's alias row) plus the
 * scope it covers, for rules no text on the latest run used. */
function RuleRow({
  alias,
  lots,
  editable,
  isMobile,
  dealerGroupId,
  onStructureChanged,
  onError,
}: {
  alias: PhotoListStargardAlias
  lots: RealLot[]
  editable: boolean
  isMobile: boolean
  dealerGroupId: number
  onStructureChanged: () => void | Promise<void>
  onError: (message: string | null) => void
}) {
  const [textDraft, setTextDraft] = useState(alias.raw_text)
  const scopeText = scopeLabel(
    alias.stargard_rooftop ? "this_file" : "every_store", alias.stargard_rooftop,
  )
  React.useEffect(() => setTextDraft(alias.raw_text), [alias.raw_text])

  const save = async (updates: { raw_text?: string; lot_id?: number }) => {
    if (alias.id === null) return
    onError(null)
    try {
      await updateStargardAlias(alias.id, updates, dealerGroupId)
      await onStructureChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to save the rule")
      setTextDraft(alias.raw_text)
    }
  }

  const handleTextBlur = () => {
    const rawText = textDraft.trim()
    if (!rawText || rawText.toLowerCase() === alias.raw_text) {
      setTextDraft(alias.raw_text)
      return
    }
    save({ raw_text: rawText })
  }

  const handleDelete = async () => {
    if (alias.id === null) return
    // Names the scope — "main lot" can have one rule per store.
    if (!window.confirm(`Delete the rule for "${alias.raw_text}" (${scopeText})?`)) return
    onError(null)
    try {
      await deleteStargardAlias(alias.id, dealerGroupId)
      await onStructureChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to delete the rule")
    }
  }

  const textInput = (
    <input
      type="text"
      value={textDraft}
      disabled={!editable}
      onChange={(e) => setTextDraft(e.target.value)}
      onBlur={handleTextBlur}
      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      aria-label={`Stargard text ${alias.raw_text}`}
      style={{ ...monoInputStyle, width: "100%" }}
    />
  )
  const lotSelect = (
    <select
      value={alias.lot_id ?? ""}
      disabled={!editable}
      onChange={(e) => save({ lot_id: Number.parseInt(e.target.value, 10) })}
      aria-label={`Lot for ${alias.raw_text}`}
      style={{ ...inputStyle, width: "100%" }}
    >
      {alias.lot_id === null && <option value="">{alias.lot_name}</option>}
      {lots.map((lot) => (
        <option key={lot.id} value={lot.id}>
          {lot.name}
        </option>
      ))}
    </select>
  )
  const trashButton = editable ? (
    <button
      type="button"
      onClick={handleDelete}
      aria-label={`Delete rule ${alias.raw_text}`}
      style={{ display: "inline-flex", padding: "2px 6px", background: "transparent", border: "none", cursor: "pointer", color: THEME.colors.semantic.danger }}
    >
      <Trash2 size={15} strokeWidth={2} />
    </button>
  ) : (
    <span />
  )
  const rowBorder = `1px solid ${THEME.colors.silver}`

  if (isMobile) {
    // Two lines on a phone: the text, then its lot + scope + delete — the
    // lot and scope are what tell two "main lot" rules apart.
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: THEME.spacing.xs, padding: `${THEME.spacing.sm} 0`, borderTop: rowBorder }}>
        {textInput}
        <div style={{ display: "flex", alignItems: "center", gap: THEME.spacing.sm }}>
          <div style={{ flex: 1, minWidth: 0 }}>{lotSelect}</div>
          <span style={{ ...cellTextStyle, whiteSpace: "nowrap" }}>{scopeText}</span>
          {trashButton}
        </div>
      </div>
    )
  }
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr) 120px 40px",
        gap: THEME.spacing.sm,
        alignItems: "center",
        padding: `${THEME.spacing.xs} 0`,
        borderTop: rowBorder,
      }}
    >
      {textInput}
      {lotSelect}
      <span style={cellTextStyle}>{scopeText}</span>
      {trashButton}
    </div>
  )
}

function AddRuleForm({
  lots,
  stargardFileOptions,
  isMobile,
  dealerGroupId,
  onStructureChanged,
  onError,
}: {
  lots: RealLot[]
  stargardFileOptions: string[]
  isMobile: boolean
  dealerGroupId: number
  onStructureChanged: () => void | Promise<void>
  onError: (message: string | null) => void
}) {
  const [text, setText] = useState("")
  const [lotId, setLotId] = useState("")
  const [scope, setScope] = useState(EVERY_STORE_FILE)
  const [adding, setAdding] = useState(false)
  const disabled = !text.trim() || !lotId || adding

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    const parsed = Number.parseInt(lotId, 10)
    if (!text.trim() || Number.isNaN(parsed)) return
    setAdding(true)
    onError(null)
    try {
      await createStargardAlias(text.trim(), parsed, scope, dealerGroupId)
      // Reset only on success — a failed add keeps the entry for a retry.
      setText("")
      setLotId("")
      setScope(EVERY_STORE_FILE)
      await onStructureChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to add the rule")
    } finally {
      setAdding(false)
    }
  }

  return (
    <form
      onSubmit={handleAdd}
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: THEME.spacing.sm,
        marginTop: THEME.spacing.md,
      }}
    >
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Add a rule: Stargard text, exactly as the file says it"
        style={{ ...monoInputStyle, flex: 1 }}
      />
      <select value={lotId} onChange={(e) => setLotId(e.target.value)} aria-label="Lot for the new rule" style={inputStyle}>
        <option value="">Pick a lot…</option>
        {lots.map((lot) => (
          <option key={lot.id} value={lot.id}>
            {lot.name}
          </option>
        ))}
      </select>
      <select value={scope} onChange={(e) => setScope(e.target.value)} aria-label="Applies to" style={inputStyle}>
        <option value={EVERY_STORE_FILE}>Every store's file</option>
        {stargardFileOptions.map((file) => (
          <option key={file} value={file}>
            Only {file}'s file
          </option>
        ))}
      </select>
      <button type="submit" disabled={disabled} style={addButtonStyle(disabled)}>
        {adding ? "Adding…" : "Add rule"}
      </button>
    </form>
  )
}
